import { useSearchParams, useLocation } from "react-router-dom";

const {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
} = require("react");

const housingInspectionContext = createContext();

const initialState = {
  selectedInspections: [],
  type: null,
};

const appPlatformFromPathname = (pathname) =>
  pathname.match(/^\/app\/([^/]+)/)?.[1];

const HousingInspectionProvider = ({ children }) => {
  const [inspectionState, setInspectionState] = useState(initialState);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const prevAppPlatformRef = useRef(undefined);

  const currentTab = searchParams.get("tab") || "all";
  const fallbackTab = searchParams.get("fallback") || currentTab;

  useEffect(() => {
    const appPlatform = appPlatformFromPathname(location.pathname);
    const prev = prevAppPlatformRef.current;

    if (prev !== undefined && prev !== appPlatform) {
      setInspectionState(initialState);
      setSearchParams(
        (params) => {
          if (!params.has("tab") && !params.has("fallback")) {
            return params;
          }
          const next = new URLSearchParams(params);
          next.delete("tab");
          next.delete("fallback");
          return next;
        },
        { replace: true }
      );
    }

    prevAppPlatformRef.current = appPlatform;
    // Intentionally only `location.pathname`: avoid effect churn from search-only updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setSearchParams is stable from react-router
  }, [location.pathname]);

  const clearSelectedInspections = () => {
    setInspectionState(initialState);
    outFromSelectionMode();
  };

  const goToSelectionMode = () => {
    if (!["not_registered", "completed"].includes(currentTab)) {
      setSearchParams({ tab: "not_registered", fallback: currentTab });
    }
  };

  const outFromSelectionMode = () => {
    setSearchParams({ tab: fallbackTab });
    searchParams.delete("fallback");
  };

  const addSelectedInspection = (inspection, state) => {
    setInspectionState((prevState) => {
      const prev = prevState.selectedInspections;

      if (!prev.length) {
        goToSelectionMode();
        window.scroll(0, 0);
      }

      return {
        ...prevState,
        selectedInspections: [...prev, inspection],
        ...state,
      };
    });
  };

  const removeSelectedInspection = (inspection) => {
    setInspectionState((prevState) => {
      const prev = prevState.selectedInspections;

      const newInspections = prev.filter(
        (i) => i.house.id !== inspection.house.id
      );

      if (!newInspections.length) {
        outFromSelectionMode();
      }

      return { ...prevState, selectedInspections: newInspections };
    });
  };

  const handleTabChange = (key) => {
    setSearchParams({ tab: key });
  };
  const value = {
    inspectionState,
    searchParams,
    clearSelectedInspections,
    addSelectedInspection,
    removeSelectedInspection,
    handleTabChange,
    setInspectionState,
  };

  return (
    <housingInspectionContext.Provider value={value}>
      {children}
    </housingInspectionContext.Provider>
  );
};

export default HousingInspectionProvider;
export const useHousingInspectionContext = () => {
  return useContext(housingInspectionContext);
};
